/**
 * Core operating loop. One implementation, three doors:
 * shelf (write a brief when a document, thread, or inbox item is saved),
 * answer (Front Desk before every user-facing model call),
 * retrieval (open full chunks only for shelves the brief names).
 * House rules version "1" is part of the Answer Drawer key.
 */
export const HOUSE_RULES_VERSION = "1";

export const HOUSE_RULES_TEXT = [
  "Answer the job-sheet question. Do not invent client names or secrets.",
  "When shelf briefs are supplied, use them and do not contradict them.",
  "A general answer must not quote a client or restricted shelf.",
  "If no brief covers the question, answer from general knowledge.",
].join(" ");

export const FAST_MODEL = "openai/gpt-4o-mini";
export const STRONG_DEFAULT_MODEL = "anthropic/claude-sonnet-4";

export const CRISIS_STOP_MESSAGE =
  "This request was stopped before any model was called. If you or someone else is in immediate danger, contact local emergency services.";

export const PRIVATE_HOLD_MESSAGE =
  "This shelf is marked client or restricted, and no private model is configured, so nothing was sent to a public model.";

export const INVALID_SHEET_MESSAGE = "This request was not sent to a model because the job sheet was invalid.";

export type Sensitivity = "general" | "client" | "restricted";
export type ModelTier = "fast" | "strong" | "private";
export type ReceiptPath = "drawer" | "crisis_stop" | "private_hold" | "ai_room";
export type FrontDeskPath = "crisis_stop" | "drawer" | "continue";

export type ShelfBrief = {
  documentId: string;
  whatItIs: string;
  summary: string;
  tags: string[];
  sensitivity: Sensitivity;
};

export type WorkOrderSource = { title: string; ref: string };

export type JobSheetInput = {
  question?: string;
  shelves?: string[];
  outsideInfo: boolean;
  sensitivity: Sensitivity;
  tier?: ModelTier;
  request?: string;
  goal?: string;
  shelves_used?: string[];
  sources?: WorkOrderSource[];
  key_facts?: string[];
  constraints?: string[];
  acceptance_criteria?: string[];
  model_tier?: ModelTier;
  budget_tokens?: number;
};

export type JobSheet = {
  question: string;
  request: string;
  goal: string;
  shelves: string[];
  shelves_used: string[];
  outsideInfo: boolean;
  sensitivity: Sensitivity;
  tier: ModelTier;
  model_tier: ModelTier;
  sources: WorkOrderSource[];
  key_facts: string[];
  constraints: string[];
  acceptance_criteria: string[];
  budget_tokens: number;
};

export class JobSheetError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "JobSheetError";
  }
}

const CRISIS_RE = /\b(kill myself|suicide|end my life|want to die)\b/i;
const OUTSIDE_RE = /\b(latest|today|current news|right now|weather|stock price)\b/i;
const FAST_RE = /^(what|who|when|where|define)\b/i;

export function isCrisisRequest(question: string): boolean {
  return CRISIS_RE.test(question);
}

/** Secret, key, and password do not route. Only an explicit crisis phrase stops. */
export function frontDesk(input: { question: string; drawerHit: boolean }): FrontDeskPath {
  if (isCrisisRequest(input.question)) return "crisis_stop";
  if (input.drawerHit) return "drawer";
  return "continue";
}

export function normalizeQuestion(question: string): string {
  return question
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function drawerKey(question: string, shelfIds: string[], houseRulesVersion = HOUSE_RULES_VERSION): string {
  const shelves = [...shelfIds].map((id) => id.trim()).filter(Boolean).sort();
  return fnv1a(`${houseRulesVersion}|${shelves.join(",")}|${normalizeQuestion(question)}`);
}

export function threadShelfId(threadId: string): string {
  return `thread:${threadId}`;
}

export function needsOutsideInfo(question: string): boolean {
  return OUTSIDE_RE.test(question);
}

export function isFastLookup(question: string, maxChars = 120): boolean {
  const trimmed = question.trim();
  const limit = Number.isFinite(maxChars) && maxChars > 0 ? maxChars : 120;
  return trimmed.length > 0 && trimmed.length < limit && FAST_RE.test(trimmed);
}

function asStringList(value: unknown, limit: number): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item).trim()).filter(Boolean).slice(0, limit);
}

function asSources(value: unknown): WorkOrderSource[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const row = item as { title?: unknown; ref?: unknown };
      const title = String(row.title ?? "").trim();
      const ref = String(row.ref ?? "").trim();
      if (!title && !ref) return null;
      return { title: title || ref, ref: ref || title };
    })
    .filter((item): item is WorkOrderSource => item !== null)
    .slice(0, 20);
}

export function validateJobSheet(input: JobSheetInput): JobSheet {
  const question = String(input.request ?? input.question ?? "").trim();
  if (!question || question.length > 8000) {
    throw new JobSheetError("Job sheet question is missing or too long.");
  }
  const rawShelves = input.shelves_used ?? input.shelves;
  if (!Array.isArray(rawShelves) || rawShelves.length > 50) {
    throw new JobSheetError("Job sheet shelves are invalid.");
  }
  const shelves = rawShelves.map((id) => String(id).trim()).filter(Boolean);
  if (shelves.length !== rawShelves.length) {
    throw new JobSheetError("Job sheet shelves must be non-empty ids.");
  }
  if (input.sensitivity !== "general" && input.sensitivity !== "client" && input.sensitivity !== "restricted") {
    throw new JobSheetError("Job sheet sensitivity is invalid.");
  }
  const tier = input.model_tier ?? input.tier;
  if (tier !== "fast" && tier !== "strong" && tier !== "private") {
    throw new JobSheetError("Job sheet tier is invalid.");
  }
  if (input.sensitivity !== "general" && shelves.length === 0) {
    throw new JobSheetError("A client or restricted job sheet needs a shelf.");
  }
  if (typeof input.outsideInfo !== "boolean") {
    throw new JobSheetError("Job sheet outside-info flag is invalid.");
  }
  const budget = input.budget_tokens ?? (tier === "fast" ? 800 : 4000);
  if (!Number.isFinite(budget) || budget < 1 || budget > 200_000) {
    throw new JobSheetError("Job sheet budget_tokens is invalid.");
  }
  return {
    question,
    request: question,
    goal: String(input.goal ?? question).trim() || question,
    shelves,
    shelves_used: shelves,
    outsideInfo: input.outsideInfo,
    sensitivity: input.sensitivity,
    tier,
    model_tier: tier,
    sources: asSources(input.sources),
    key_facts: asStringList(input.key_facts, 12),
    constraints: asStringList(input.constraints, 12),
    acceptance_criteria: asStringList(input.acceptance_criteria, 12),
    budget_tokens: Math.floor(budget),
  };
}

export function shelfSensitivity(briefs: ShelfBrief[]): Sensitivity {
  if (briefs.some((brief) => brief.sensitivity === "restricted")) return "restricted";
  if (briefs.some((brief) => brief.sensitivity === "client")) return "client";
  return "general";
}

/** General prompts receive general briefs only. */
export function briefsForPrompt(briefs: ShelfBrief[], sensitivity: Sensitivity): ShelfBrief[] {
  if (sensitivity !== "general") return briefs;
  return briefs.filter((brief) => brief.sensitivity === "general");
}

export function switchboard(input: {
  autoSelectBest: boolean;
  pickedModel: string;
  sensitivity: Sensitivity;
  privateModel: string | null;
  question: string;
  fastMaxChars?: number;
}): { tier: ModelTier; model: string | null; stop: "private_hold" | null } {
  if (input.sensitivity !== "general") {
    const privateModel = input.privateModel?.trim() || null;
    if (!privateModel) return { tier: "private", model: null, stop: "private_hold" };
    return { tier: "private", model: privateModel, stop: null };
  }
  const picked = input.pickedModel.trim();
  if (!input.autoSelectBest) {
    return { tier: "strong", model: picked || FAST_MODEL, stop: null };
  }
  if (isFastLookup(input.question, input.fastMaxChars)) {
    return { tier: "fast", model: FAST_MODEL, stop: null };
  }
  return { tier: "strong", model: picked || STRONG_DEFAULT_MODEL, stop: null };
}

export function checkTheWork(input: {
  answer: string;
  question: string;
  sensitivity: Sensitivity;
  clientExcerpts: string[];
}): { ok: boolean; reason?: "empty" | "client_leak" } {
  const answer = input.answer.trim();
  if (!input.question.trim() || !answer) return { ok: false, reason: "empty" };
  if (input.sensitivity === "general") {
    for (const excerpt of input.clientExcerpts) {
      const needle = excerpt.trim();
      if (needle.length >= 40 && answer.includes(needle)) {
        return { ok: false, reason: "client_leak" };
      }
    }
  }
  return { ok: true };
}

export function receiptLine(path: ReceiptPath, model: string | null, totalTokens: number | null): string {
  if (path === "drawer") return "Answer Drawer · $0 · no model";
  if (path === "crisis_stop") return "Stopped · no model";
  if (path === "private_hold") return "Private room unavailable · no model";
  const tokens = totalTokens == null ? "—" : String(totalTokens);
  return `AI Room · ${model || "model"} · ${tokens} tokens`;
}

export function parseBriefJson(raw: string): { whatItIs: string; summary: string; tags: string[]; sensitivity: Sensitivity } | null {
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  try {
    const parsed = JSON.parse(raw.slice(start, end + 1)) as Record<string, unknown>;
    const summary = String(parsed.summary ?? "").trim();
    if (!summary) return null;
    const sensitivity: Sensitivity =
      parsed.sensitivity === "client" || parsed.sensitivity === "restricted" ? parsed.sensitivity : "general";
    const tags = Array.isArray(parsed.tags) ? parsed.tags.map(String).slice(0, 12) : [];
    return { whatItIs: String(parsed.whatItIs ?? parsed.what_it_is ?? "Note").trim() || "Note", summary, tags, sensitivity };
  } catch {
    return null;
  }
}

export function formatWorkOrderBlock(sheet: JobSheet): string {
  const lines = [
    HOUSE_RULES_TEXT,
    "",
    "Work order. Answer from this brief only.",
    `Request: ${sheet.request}`,
    `Goal: ${sheet.goal}`,
    `Shelves: ${sheet.shelves_used.join(", ") || "general"}`,
    `Tier: ${sheet.model_tier}`,
    `Budget tokens: ${sheet.budget_tokens}`,
  ];
  if (sheet.key_facts.length) lines.push(`Key facts:\n${sheet.key_facts.map((fact) => `- ${fact}`).join("\n")}`);
  if (sheet.constraints.length) lines.push(`Constraints:\n${sheet.constraints.map((item) => `- ${item}`).join("\n")}`);
  if (sheet.acceptance_criteria.length) {
    lines.push(`Acceptance:\n${sheet.acceptance_criteria.map((item) => `- ${item}`).join("\n")}`);
  }
  if (sheet.sources.length) {
    lines.push(`Sources:\n${sheet.sources.map((source) => `- ${source.title} (${source.ref})`).join("\n")}`);
  }
  return lines.join("\n");
}

export function formatBriefBlock(briefs: ShelfBrief[]): string {
  const lines = briefs.map((brief) => {
    const tags = brief.tags.length ? ` Tags: ${brief.tags.join(", ")}.` : "";
    return `- ${brief.whatItIs || "Shelf"} (${brief.documentId}): ${brief.summary}${tags}`;
  });
  const body = lines.length ? `\n\nShelf briefs:\n${lines.join("\n")}` : "";
  return `${HOUSE_RULES_TEXT}${body}`;
}

export function isDocumentShelfId(id: string): boolean {
  return Boolean(id) && !id.includes(":");
}
